// AF-3.16 — shared workspace + entity selector bar for every Brand
// Growth OS page. The selected workspace/entity always comes from the
// authenticated clients/entities list -- never parsed from the URL.
//
// P1.5: `entityTypes` is an optional override of the selectable entity-type
// list, defaulting to the original AF-3.16 set byte-for-byte -- every
// existing caller that doesn't pass it sees zero behavior change. Added so
// OpportunityPortfolioPage (the one page whose backend view -- AF-3.11's
// get_opportunities_view() -- reads directly from the entity-type-agnostic
// AF-2.4 OpportunityService) can also offer "brand", the entity type the
// SoM -> Growth Intelligence bridge actually attaches its opportunities to.
// No other page passes this prop, so no other page's selectable types change.

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const DEFAULT_ENTITY_TYPES = ["product", "campaign", "creative", "channel", "audience"];

// P1.7: extracted (byte-identical markup/testid) so workspace-wide pages
// that don't need the entity-type/entity drill-down below -- Signals,
// Recommendations, Action Plans -- can reuse the exact same client
// switcher without pulling in this bar's other two selects.
export function WorkspaceSelect({ clients, workspaceId, setWorkspaceId }) {
  return (
    <Select value={workspaceId} onValueChange={setWorkspaceId}>
      <SelectTrigger data-testid="workspace-select" className="w-[200px] h-8 text-[11px] bg-[var(--surface)] border-[var(--border)] text-[var(--text-primary)]">
        <SelectValue placeholder="Select workspace" />
      </SelectTrigger>
      <SelectContent className="bg-[var(--surface)] border-[var(--border)] text-[var(--text-primary)]">
        {/* Batch 1 (identity audit closure) -- GET /clients now returns a
            real display_name (company_context.company_name when Company
            Discovery found one, P2.1/P2.2), falling back to the raw
            client.name byte-for-byte. This is the single most-reused
            client picker in the app (Signals/Diagnoses/Recommendations/
            Action Plans/Company Context/AI Discovery/...), so fixing it
            here fixes all of them at once. */}
        {clients.map((c) => <SelectItem key={c.id} value={c.id} className="text-[11px]">{c.display_name || c.name || c.id}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export function BrandContextBar({ ctx, entityTypes = DEFAULT_ENTITY_TYPES }) {
  const { clients, workspaceId, setWorkspaceId, entities, entityType, setEntityType, entityId, setEntityId, loadingEntities } = ctx;

  return (
    <div className="flex items-center gap-2 flex-wrap mb-4" data-testid="brand-context-bar">
      <WorkspaceSelect clients={clients} workspaceId={workspaceId} setWorkspaceId={setWorkspaceId} />

      <Select value={entityType} onValueChange={setEntityType}>
        <SelectTrigger data-testid="entity-type-select" className="w-[130px] h-8 text-[11px] bg-[var(--surface)] border-[var(--border)] text-[var(--text-primary)]">
          <SelectValue placeholder="Entity type" />
        </SelectTrigger>
        <SelectContent className="bg-[var(--surface)] border-[var(--border)] text-[var(--text-primary)]">
          {entityTypes.map((t) => (
            <SelectItem key={t} value={t} className="text-[11px] capitalize">{t}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select value={entityId} onValueChange={setEntityId} disabled={loadingEntities || entities.length === 0}>
        <SelectTrigger data-testid="entity-select" className="w-[220px] h-8 text-[11px] bg-[var(--surface)] border-[var(--border)] text-[var(--text-primary)]">
          <SelectValue placeholder={loadingEntities ? "Loading…" : entities.length === 0 ? "No entities" : "Select entity"} />
        </SelectTrigger>
        <SelectContent className="bg-[var(--surface)] border-[var(--border)] text-[var(--text-primary)]">
          {entities.map((e) => <SelectItem key={e.id} value={e.id} className="text-[11px]">{e.canonical_name || e.id}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
